/**
 * Normalise a YouTube URL to its privacy-safe embed form (RACCPE-12).
 *
 * YouTube refuses to render a `watch?v=…` or `youtu.be/…` URL inside an
 * `<iframe>` (X-Frame-Options / frame-ancestors), so the trainee player must
 * embed `https://www.youtube.com/embed/<id>` instead. Authors store the URL
 * verbatim (informational-only block, never gating), so we normalise at render.
 *
 * Maps:
 *   - youtube.com/watch?v=<id>  → youtube.com/embed/<id>
 *   - youtu.be/<id>             → youtube.com/embed/<id>
 *   - already-/embed/<id>       → unchanged
 * A `t`/`start` timestamp (e.g. `t=90`, `t=1m30s`) is preserved as `start=<s>`.
 * Anything unrecognised (or unparseable) is returned unchanged.
 */
export function toYouTubeEmbedUrl(rawUrl: string): string {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return rawUrl;
  }

  const host = parsed.hostname.replace(/^www\./, "");
  let videoId: string | null = null;

  if (host === "youtu.be") {
    videoId = parsed.pathname.slice(1).split("/")[0] || null;
  } else if (host === "youtube.com" || host === "m.youtube.com") {
    if (parsed.pathname === "/watch") {
      videoId = parsed.searchParams.get("v");
    } else if (parsed.pathname.startsWith("/embed/")) {
      // Already an embed URL — leave it untouched.
      return rawUrl;
    }
  }

  if (!videoId) return rawUrl;

  const embed = new URL(`https://www.youtube.com/embed/${videoId}`);
  const start = parseStartSeconds(parsed);
  if (start != null) embed.searchParams.set("start", String(start));
  return embed.toString();
}

/**
 * Parse a YouTube `t`/`start` timestamp into whole seconds. Accepts bare
 * seconds (`90`), a trailing-`s` form (`90s`), and the `1h2m3s` form. Returns
 * null when absent or unparseable.
 */
function parseStartSeconds(parsed: URL): number | null {
  const raw = parsed.searchParams.get("t") ?? parsed.searchParams.get("start");
  if (!raw) return null;

  if (/^\d+$/.test(raw)) return Number.parseInt(raw, 10);

  const match = raw.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
  if (!match || (!match[1] && !match[2] && !match[3])) return null;
  const hours = Number.parseInt(match[1] ?? "0", 10);
  const minutes = Number.parseInt(match[2] ?? "0", 10);
  const seconds = Number.parseInt(match[3] ?? "0", 10);
  return hours * 3600 + minutes * 60 + seconds;
}
